<template>
    <div class="ds-wrap h-full w-full flex flex-col">
        <div class="ds-toolbar">
            <div class="ds-toolbar__row ds-toolbar__row--between">
                <p class="ds-hint">
                    {{ entity }} · {{ campus }}
                    <span v-if="!showList && currentSemester()"> · Semester {{ currentSemester() }}</span>
                </p>
                <div v-if="showList && canUpdate" class="ds-toolbar__actions">
                    <button type="button" class="ds-btn ds-btn--primary" @click="openCreate">
                        <font-awesome-icon :icon="['fas', 'plus']" class="mr-2" />
                        Add configuration
                    </button>
                </div>
                <div v-else-if="!showList" class="ds-toolbar__actions">
                    <button v-if="filterAll" type="button" class="ds-btn ds-btn--ghost" @click="backToList">
                        Back to list
                    </button>
                    <button v-else-if="creatingNew" type="button" class="ds-btn ds-btn--ghost" @click="cancelCreate">
                        Cancel
                    </button>
                    <button
                        v-if="canUpdate && !creatingNew && editingId"
                        type="button"
                        class="ds-btn ds-btn--copy"
                        :disabled="copying"
                        @click="openCopySemester(currentSemester())"
                    >
                        <font-awesome-icon :icon="['fas', 'copy']" class="mr-2" />
                        {{ copying ? "Copying..." : "Copy to new semester" }}
                    </button>
                    <button
                        v-if="canDeleteCurrentSetting"
                        type="button"
                        class="ds-btn ds-btn--danger"
                        :disabled="deleting"
                        @click="confirmDeleteCurrentSetting"
                    >
                        <font-awesome-icon icon="trash" class="mr-2" />
                        {{ deleting ? "Deleting..." : "Delete configuration" }}
                    </button>
                    <button
                        v-if="canUpdate && !filterAll && !creatingNew"
                        type="button"
                        class="ds-btn ds-btn--primary"
                        @click="openCreate"
                    >
                        <font-awesome-icon :icon="['fas', 'plus']" class="mr-2" />
                        Add Configuration
                    </button>
                </div>
            </div>
            <div v-if="showList" class="ds-toolbar__row mt-3">
                <div class="ds-search">
                    <search ref="searchRef" @on-search="onSearch" class-list="px-3 h-9"></search>
                </div>
            </div>
        </div>

        <div class="flex-auto flex flex-col px-4 sm:px-8 pb-8">
            <template v-if="showList">
                <div v-if="listLoading" class="flex-auto flex flex-col">
                    <table-shimmer :titleHeaders="tableHeaders" :rows="perPage" />
                </div>

                <div v-else-if="items.length === 0" class="flex-auto flex justify-center items-center py-16">
                    <div class="text-center max-w-md px-4">
                        <div class="ds-empty-icon">⚙️</div>
                        <h2 class="text-xl font-semibold text-gray-800 mb-2">No configurations yet</h2>
                        <p class="text-gray-500 mb-4">Add a semester configuration to start managing accommodation.</p>
                        <button v-if="canUpdate" type="button" class="ds-btn ds-btn--primary" @click="openCreate">
                            Add configuration
                        </button>
                    </div>
                </div>

                <template v-else>
                    <div class="ds-table-shell overflow-x-auto">
                        <table class="min-w-full text-left text-sm">
                            <thead>
                                <tr>
                                    <th class="ds-th w-12">#</th>
                                    <th class="ds-th">Semester</th>
                                    <th class="ds-th">Villas</th>
                                    <th class="ds-th">Capacity</th>
                                    <th class="ds-th">Layout</th>
                                    <th class="ds-th">Identical</th>
                                    <th v-if="canUpdate || canAssign || canDelete" class="ds-th text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(item, index) in items" :key="item.id" class="ds-row">
                                    <td class="ds-td text-gray-400">{{ rowNumber(index) }}</td>
                                    <td class="ds-td font-semibold">{{ item.semester }}</td>
                                    <td class="ds-td">{{ item.villa_count }}</td>
                                    <td class="ds-td">{{ item.total_capacity }}</td>
                                    <td class="ds-td">{{ layoutLabel(item) }}</td>
                                    <td class="ds-td">{{ item.villas_identical ? "Yes" : "No" }}</td>
                                    <td v-if="canUpdate || canAssign || canDelete" class="ds-td text-right whitespace-nowrap">
                                        <div class="ds-actions">
                                            <button
                                                v-if="canAssign"
                                                type="button"
                                                title="Assign students"
                                                class="ds-icon-btn"
                                                @click="$emit('open-assignments', item.semester)"
                                            >
                                                <font-awesome-icon :icon="['fas', 'user-plus']" class="w-3 h-3 text-gray-700" />
                                            </button>
                                            <button
                                                v-if="canUpdate"
                                                type="button"
                                                title="Copy to new semester"
                                                class="ds-icon-btn ds-icon-btn--copy"
                                                :disabled="copying"
                                                @click="openCopySemester(item.semester)"
                                            >
                                                <font-awesome-icon :icon="['fas', 'copy']" class="w-3 h-3" />
                                            </button>
                                            <button
                                                v-if="canUpdate"
                                                type="button"
                                                title="Edit"
                                                class="ds-icon-btn"
                                                @click="openEdit(item)"
                                            >
                                                <font-awesome-icon icon="pen" class="w-3 h-3 text-gray-700" />
                                            </button>
                                            <button
                                                v-if="canDeleteItem(item)"
                                                type="button"
                                                title="Delete"
                                                class="ds-icon-btn ds-icon-btn--danger"
                                                :disabled="deleting"
                                                @click="confirmDeleteSetting(item)"
                                            >
                                                <font-awesome-icon icon="trash" class="w-3 h-3 text-gray-700" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div class="mt-4" v-if="paginatorInfoWithItems && paginatorInfoWithItems.total">
                        <pagination :paginatorInfo="paginatorInfoWithItems" :setPage="setPage"></pagination>
                    </div>
                </template>
            </template>

            <template v-else>
                <div v-if="loading" class="flex justify-center py-16 w-full">
                    <base-page-spinner type="spin" class-list="h-12"></base-page-spinner>
                </div>

                <form v-else class="ds-form bg-white rounded-xl border shadow-sm p-6 w-full max-w-3xl mx-auto" @submit.prevent="save">
                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div v-if="filterAll || creatingNew" class="sm:col-span-2">
                            <label class="ds-label">Semester <span class="text-red-500">*</span></label>
                            <input
                                v-model="formSemester"
                                type="text"
                                class="ds-input"
                                placeholder="e.g. 202610"
                                :readonly="!!editingId"
                                required
                            />
                        </div>
                        <div class="sm:col-span-2">
                            <label class="ds-label">Number of villas <span class="text-red-500">*</span></label>
                            <input
                                v-model="form.villa_count"
                                type="number"
                                min="1"
                                max="99"
                                class="ds-input"
                                required
                                @input="onVillaCountChange"
                            />
                        </div>
                        <div class="sm:col-span-2">
                            <label class="ds-label">Do all villas have the same rooms and beds? <span class="text-red-500">*</span></label>
                            <div class="ds-choice">
                                <button
                                    type="button"
                                    class="ds-choice__btn"
                                    :class="{ 'ds-choice__btn--on': form.villas_identical }"
                                    @click="setIdentical(true)"
                                >
                                    Yes, they are all the same
                                </button>
                                <button
                                    type="button"
                                    class="ds-choice__btn"
                                    :class="{ 'ds-choice__btn--on': !form.villas_identical }"
                                    @click="setIdentical(false)"
                                >
                                    No, villas are different
                                </button>
                            </div>
                        </div>

                        <div v-if="form.villas_identical" class="sm:col-span-2">
                            <label class="ds-label">Beds in each room <span class="text-red-500">*</span></label>
                            <input
                                v-model="form.room_bed_layout"
                                type="text"
                                class="ds-input"
                                placeholder="2+2+2+3+2+1"
                                required
                            />
                            <p class="ds-help">
                                Enter beds per room in order, separated by +. Example: 2+2+2+3+2+1 means Room 1 has 2 beds, Room 4 has 3 beds, Room 6 has 1 bed. This layout is copied to every villa.
                            </p>
                            <div v-if="layoutPreview.length" class="ds-preview">
                                <div v-for="row in layoutPreview" :key="'prev-' + row.room_no" class="ds-preview__row">
                                    Room {{ row.room_no }} — {{ row.bed_count }} {{ row.bed_count === 1 ? "bed" : "beds" }}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-if="!form.villas_identical" class="mt-4">
                        <div v-for="villa in form.villas" :key="'villa-' + villa.villa_no" class="ds-villa">
                            <h3 class="ds-villa__title">Villa {{ villa.villa_no }}</h3>
                            <div class="mb-3">
                                <label class="ds-label">How many rooms in Villa {{ villa.villa_no }}? <span class="text-red-500">*</span></label>
                                <input
                                    v-model="villa.room_count"
                                    type="number"
                                    min="1"
                                    max="30"
                                    class="ds-input"
                                    required
                                    @input="onVillaRoomCountChange(villa)"
                                />
                            </div>
                            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div v-for="room in villa.rooms" :key="'v' + villa.villa_no + '-r' + room.room_no">
                                    <label class="ds-label">
                                        How many beds in Villa {{ villa.villa_no }} Room {{ room.room_no }}?
                                        <span class="text-red-500">*</span>
                                    </label>
                                    <input v-model="room.bed_count" type="number" min="1" max="20" class="ds-input" required />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="ds-capacity mt-5">
                        <div class="ds-capacity__label">Total accommodation capacity</div>
                        <div class="ds-capacity__value">{{ calculatedCapacity }} beds</div>
                        <div class="ds-capacity__hint">Calculated from all villas, rooms, and beds</div>
                    </div>

                    <div class="mt-4">
                        <label class="ds-label">Available rooms / beds (description)</label>
                        <textarea
                            v-model="form.available_rooms_beds"
                            rows="3"
                            class="ds-input ds-textarea"
                            placeholder="Optional notes for staff"
                        ></textarea>
                    </div>

                    <div class="mt-6 flex justify-end gap-2">
                        <button v-if="filterAll || creatingNew" type="button" class="ds-btn ds-btn--ghost" @click="filterAll ? backToList() : cancelCreate()">
                            Cancel
                        </button>
                        <button v-if="canUpdate" type="submit" class="ds-btn ds-btn--primary" :disabled="saving || calculatedCapacity < 1">
                            {{ saving ? "Saving..." : editingId ? "Update Configuration" : "Save configuration" }}
                        </button>
                    </div>
                </form>
            </template>
        </div>
    </div>
</template>

<script>
import gql from "graphql-tag";
import Pagination from "../../../../components/shared/pagination.vue";
import TableShimmer from "../../../../components/shared/TableShimmer.vue";
import Search from "../../../../components/shared/search.vue";

const SETTINGS_QUERY = gql`
    query AccommodationSettings($entity: String!, $campus: String!, $semester: String!) {
        accommodationSettings(entity: $entity, campus: $campus, semester: $semester) {
            error
            message
            data {
                id
                created_by
                villas_identical
                villa_count
                total_capacity
                room_bed_layout
                available_rooms_beds
                villas {
                    villa_no
                    rooms {
                        room_no
                        bed_count
                    }
                }
            }
        }
    }
`;

const SETTINGS_LIST_QUERY = gql`
    query AccommodationSettingsList($first: Int!, $page: Int!, $entity: String!, $campus: String!, $search: String) {
        accommodationSettingsList(first: $first, page: $page, entity: $entity, campus: $campus, search: $search) {
            error
            message
            data {
                id
                semester
                created_by
                villas_identical
                villa_count
                total_capacity
                rooms_per_villa
                beds_per_villa
                room_bed_layout
                available_rooms_beds
            }
            paginatorInfo {
                currentPage
                lastPage
                perPage
                total
                hasMorePages
                firstItem
                lastItem
            }
        }
    }
`;

const UPSERT_MUTATION = gql`
    mutation UpsertAccommodationSettings($input: AccommodationSettingsInput!) {
        upsertAccommodationSettings(input: $input) {
            error
            message
            data {
                id
                total_capacity
            }
        }
    }
`;

const COPY_MUTATION = gql`
    mutation CopyAccommodationSemester($input: CopyAccommodationSemesterInput!) {
        copyAccommodationSemester(input: $input) {
            error
            message
            data {
                source_semester
                target_semester
                villa_count
                bed_count
                assignments_copied
            }
        }
    }
`;

const DELETE_MUTATION = gql`
    mutation DeleteAccommodationSettings($id: ID!) {
        deleteAccommodationSettings(id: $id) {
            error
            message
        }
    }
`;

function defaultBedCounts() {
    return [2, 2, 2, 3, 2, 1];
}

function parseLayout(layout) {
    var raw = String(layout || "").trim();
    if (!raw) return [];
    var parts = raw.split("+");
    var counts = [];
    for (var i = 0; i < parts.length; i++) {
        var n = parseInt(parts[i], 10);
        if (!n || n < 1) return [];
        counts.push(n);
    }
    return counts;
}

function roomsFromCounts(counts) {
    var rooms = [];
    for (var i = 0; i < counts.length; i++) {
        rooms.push({
            room_no: i + 1,
            bed_count: String(counts[i]),
        });
    }
    return rooms;
}

function makeVilla(villaNo, bedCounts) {
    var counts = bedCounts && bedCounts.length ? bedCounts : defaultBedCounts();
    return {
        villa_no: villaNo,
        room_count: String(counts.length),
        rooms: roomsFromCounts(counts),
    };
}

function makeVillas(count, bedCounts) {
    var villas = [];
    var n = parseInt(count, 10) || 0;
    for (var i = 1; i <= n; i++) {
        villas.push(makeVilla(i, bedCounts));
    }
    return villas;
}

function emptyForm() {
    return {
        villa_count: "10",
        villas_identical: true,
        room_bed_layout: "2+2+2+3+2+1",
        available_rooms_beds: "",
        villas: makeVillas(10, defaultBedCounts()),
    };
}

export default {
    name: "AccommodationSettingsModule",
    components: { Pagination, TableShimmer, Search },
    props: {
        entity: { type: String, required: true },
        campus: { type: String, required: true },
        semester: { type: String, required: true },
        filterAll: { type: Boolean, default: false },
        prefillSemester: { type: String, default: "" },
    },
    emits: ["changed", "open-assignments"],
    data() {
        return {
            loading: false,
            listLoading: false,
            saving: false,
            copying: false,
            deleting: false,
            showForm: false,
            loadedCreatedBy: null,
            creatingNew: false,
            editingId: null,
            formSemester: "",
            form: emptyForm(),
            items: [],
            paginatorInfo: null,
            perPage: 25,
            page: 1,
            search: "",
            tableHeaders: ["#", "Semester", "Villas", "Capacity", "Layout", "Identical", "Actions"],
        };
    },
    computed: {
        canUpdate() {
            var perms = this.$store.getters.permissions || [];
            return perms.indexOf("accommodation-settings-update") !== -1 || this.$store.getters.role === "superadmin";
        },
        canDelete() {
            var perms = this.$store.getters.permissions || [];
            return perms.indexOf("accommodation-settings-delete") !== -1;
        },
        canDeleteCurrentSetting() {
            if (!this.editingId || this.creatingNew || this.filterAll) return false;
            return this.canDeleteItem({
                id: this.editingId,
                created_by: this.loadedCreatedBy,
            });
        },
        canAssign() {
            var perms = this.$store.getters.permissions || [];
            return (
                perms.indexOf("accommodation-assignments-create") !== -1 ||
                perms.indexOf("accommodation-assignments-index") !== -1 ||
                this.$store.getters.role === "superadmin"
            );
        },
        showList() {
            return this.filterAll && !this.showForm;
        },
        layoutPreview() {
            var counts = parseLayout(this.form.room_bed_layout);
            var rows = [];
            for (var i = 0; i < counts.length; i++) {
                rows.push({ room_no: i + 1, bed_count: counts[i] });
            }
            return rows;
        },
        calculatedCapacity() {
            if (this.form.villas_identical) {
                var counts = parseLayout(this.form.room_bed_layout);
                var villaCount = parseInt(this.form.villa_count, 10) || 0;
                var perVilla = 0;
                for (var i = 0; i < counts.length; i++) {
                    perVilla += counts[i];
                }
                return villaCount * perVilla;
            }
            var total = 0;
            var villas = this.form.villas || [];
            for (var v = 0; v < villas.length; v++) {
                var rooms = villas[v].rooms || [];
                for (var r = 0; r < rooms.length; r++) {
                    total += parseInt(rooms[r].bed_count, 10) || 0;
                }
            }
            return total;
        },
        paginatorInfoWithItems() {
            if (!this.paginatorInfo) return null;
            var pi = this.paginatorInfo;
            return {
                currentPage: pi.currentPage,
                lastPage: pi.lastPage,
                perPage: pi.perPage,
                total: pi.total,
                hasMorePages: pi.hasMorePages,
                firstItem: pi.firstItem || (pi.currentPage - 1) * pi.perPage + 1,
                lastItem: pi.lastItem || Math.min(pi.currentPage * pi.perPage, pi.total),
            };
        },
    },
    watch: {
        semester: function (newVal) {
            if (!this.filterAll) {
                this.formSemester = newVal || "";
                this.showForm = true;
                this.loadSettings();
            }
        },
        filterAll: function () {
            this.resetViewMode();
        },
    },
    created() {
        this.resetViewMode();
    },
    methods: {
        resetViewMode() {
            this.creatingNew = false;
            if (this.filterAll) {
                if (this.prefillSemester) {
                    this.openEdit({ id: null, semester: this.prefillSemester });
                } else {
                    this.showForm = false;
                    this.editingId = null;
                    this.formSemester = "";
                    this.form = emptyForm();
                    this.page = 1;
                    this.loadList();
                }
            } else {
                this.showForm = true;
                this.editingId = null;
                this.formSemester = this.semester || "";
                this.loadSettings();
            }
        },
        currentSemester() {
            if (this.filterAll || this.creatingNew) {
                return (this.formSemester || "").trim();
            }
            return (this.semester || "").trim();
        },
        layoutLabel(item) {
            if (item.villas_identical && item.room_bed_layout) return item.room_bed_layout;
            return "Custom per villa";
        },
        rowNumber(index) {
            var first = this.paginatorInfoWithItems ? this.paginatorInfoWithItems.firstItem : 1;
            return first + index;
        },
        onSearch(text) {
            this.search = text || "";
            this.page = 1;
            this.loadList();
        },
        setPage(p) {
            this.page = p;
            this.loadList();
        },
        loadList() {
            if (!this.entity || !this.campus) return;
            this.listLoading = true;
            this.$apollo
                .query({
                    query: SETTINGS_LIST_QUERY,
                    variables: {
                        first: this.perPage,
                        page: this.page,
                        entity: this.entity,
                        campus: this.campus,
                        search: this.search || null,
                    },
                    fetchPolicy: "network-only",
                })
                .then((resp) => {
                    var root = resp.data && resp.data.accommodationSettingsList;
                    if (root && root.error) throw new Error(root.message);
                    this.items = root && root.data ? root.data : [];
                    this.paginatorInfo = root && root.paginatorInfo ? root.paginatorInfo : null;
                })
                .catch((err) => {
                    this.items = [];
                    if (this.$swal) {
                        this.$swal.fire({
                            icon: "error",
                            title: "Error",
                            text: err && err.message ? err.message : "Failed to load configurations.",
                        });
                    }
                })
                .finally(() => {
                    this.listLoading = false;
                });
        },
        openCreate() {
            this.editingId = null;
            this.loadedCreatedBy = null;
            this.creatingNew = true;
            this.formSemester = "";
            this.form = emptyForm();
            this.showForm = true;
        },
        canDeleteItem(item) {
            if (!this.canDelete) return false;
            if (this.$store.getters.role === "superadmin") return true;
            var userId = parseInt(this.$store.getters.userId, 10);
            if (!userId || !item) return false;
            var createdBy = item.created_by != null ? parseInt(item.created_by, 10) : null;
            return createdBy === userId;
        },
        confirmDeleteSetting(item) {
            if (!item || !item.id || !this.canDeleteItem(item)) return;
            var self = this;
            if (!this.$swal) return;
            this.$swal
                .fire({
                    title: "Delete semester " + item.semester + "?",
                    text: "This removes the configuration and all assignments for this semester.",
                    icon: "warning",
                    showCancelButton: true,
                    confirmButtonColor: "#dc2626",
                    confirmButtonText: "Delete",
                })
                .then(function (result) {
                    if (result.isConfirmed) self.deleteSetting(item.id);
                });
        },
        confirmDeleteCurrentSetting() {
            if (!this.editingId) return;
            this.confirmDeleteSetting({
                id: this.editingId,
                semester: this.currentSemester(),
                created_by: this.loadedCreatedBy,
            });
        },
        deleteSetting(id) {
            var self = this;
            this.deleting = true;
            this.$apollo
                .mutate({
                    mutation: DELETE_MUTATION,
                    variables: { id: id },
                })
                .then(function (resp) {
                    var root = resp.data && resp.data.deleteAccommodationSettings;
                    if (root && root.error) throw new Error(root.message || "Delete failed.");
                    if (self.$swal) {
                        var Toast = self.$swal.mixin({
                            toast: true,
                            position: "top-end",
                            showConfirmButton: false,
                            timer: 3000,
                            timerProgressBar: true,
                        });
                        Toast.fire({
                            icon: "success",
                            title: root && root.message ? root.message : "Configuration deleted.",
                        });
                    }
                    self.editingId = null;
                    self.loadedCreatedBy = null;
                    self.$emit("changed", { configurationDeleted: !self.filterAll });
                    if (self.filterAll) {
                        self.showForm = false;
                        self.loadList();
                    }
                })
                .catch(function (err) {
                    if (self.$swal) {
                        self.$swal.fire({
                            icon: "error",
                            title: "Delete failed",
                            text: err && err.message ? err.message : "Could not delete configuration.",
                        });
                    }
                })
                .finally(function () {
                    self.deleting = false;
                });
        },
        openCopySemester(sourceSemester) {
            var self = this;
            var source = String(sourceSemester || "").trim();
            if (!source) {
                if (this.$swal) {
                    this.$swal.fire({
                        icon: "warning",
                        title: "Semester required",
                        text: "Select a source semester to copy.",
                    });
                }
                return;
            }
            if (!this.$swal) return;

            this.$swal
                .fire({
                    title: "Copy semester " + source,
                    html:
                        '<p style="margin:0 0 0.75rem;color:#64748b;font-size:0.875rem;text-align:left;">' +
                        "This copies the full configuration and all student assignments into a new semester.</p>" +
                        '<input id="acc-copy-semester" class="swal2-input" placeholder="New semester e.g. 202640" />',
                    focusConfirm: false,
                    showCancelButton: true,
                    confirmButtonText: "Copy",
                    cancelButtonText: "Cancel",
                    preConfirm: function () {
                        var el = document.getElementById("acc-copy-semester");
                        var value = el && el.value ? String(el.value).trim() : "";
                        if (!value) {
                            self.$swal.showValidationMessage("Enter the new semester code.");
                            return false;
                        }
                        if (value === source) {
                            self.$swal.showValidationMessage(
                                "New semester must be different from " + source + "."
                            );
                            return false;
                        }
                        return value;
                    },
                })
                .then(function (result) {
                    if (!result.isConfirmed || !result.value) return;
                    self.runCopySemester(source, result.value);
                });
        },
        runCopySemester(sourceSemester, targetSemester) {
            var self = this;
            this.copying = true;
            this.$apollo
                .mutate({
                    mutation: COPY_MUTATION,
                    variables: {
                        input: {
                            entity: this.entity,
                            campus: this.campus,
                            source_semester: sourceSemester,
                            target_semester: targetSemester,
                        },
                    },
                })
                .then(function (resp) {
                    var root = resp.data && resp.data.copyAccommodationSemester;
                    if (root && root.error) {
                        throw new Error(root.message || "Copy failed.");
                    }
                    var data = root && root.data ? root.data : null;
                    var msg = root && root.message ? root.message : "Copied successfully.";
                    if (self.$swal) {
                        var detail = data
                            ? "Villas: " +
                              data.villa_count +
                              " · Beds: " +
                              data.bed_count +
                              " · Assignments: " +
                              data.assignments_copied
                            : "";
                        self.$swal.fire({
                            icon: "success",
                            title: "Semester copied",
                            text: detail ? msg + " " + detail : msg,
                        });
                    }
                    self.$emit("changed");
                    if (self.filterAll) {
                        self.loadList();
                    }
                })
                .catch(function (err) {
                    if (self.$swal) {
                        self.$swal.fire({
                            icon: "error",
                            title: "Copy failed",
                            text: err && err.message ? err.message : "Could not copy semester.",
                        });
                    }
                })
                .finally(function () {
                    self.copying = false;
                });
        },
        openEdit(item) {
            this.creatingNew = false;
            this.editingId = item && item.id ? item.id : null;
            this.loadedCreatedBy = item && item.created_by != null ? item.created_by : null;
            this.formSemester = item && item.semester ? item.semester : "";
            this.showForm = true;
            this.loadSettings();
        },
        backToList() {
            this.showForm = false;
            this.creatingNew = false;
            this.editingId = null;
            this.formSemester = "";
            this.form = emptyForm();
            this.loadList();
        },
        cancelCreate() {
            this.creatingNew = false;
            this.editingId = null;
            this.formSemester = this.semester || "";
            this.form = emptyForm();
            this.loadSettings();
        },
        setIdentical(value) {
            this.form.villas_identical = value;
            if (!value) {
                this.syncVillaList(parseInt(this.form.villa_count, 10) || 0, parseLayout(this.form.room_bed_layout));
            }
        },
        onVillaCountChange() {
            var count = parseInt(this.form.villa_count, 10) || 0;
            if (count < 1) return;
            this.syncVillaList(count, parseLayout(this.form.room_bed_layout));
        },
        syncVillaList(count, bedCounts) {
            var existing = this.form.villas || [];
            var next = [];
            for (var i = 1; i <= count; i++) {
                var found = null;
                for (var j = 0; j < existing.length; j++) {
                    if (existing[j].villa_no === i) {
                        found = existing[j];
                        break;
                    }
                }
                next.push(found ? found : makeVilla(i, bedCounts));
            }
            this.form.villas = next;
        },
        onVillaRoomCountChange(villa) {
            var count = parseInt(villa.room_count, 10) || 0;
            if (count < 1) return;
            var existing = villa.rooms || [];
            var next = [];
            for (var i = 1; i <= count; i++) {
                var found = null;
                for (var j = 0; j < existing.length; j++) {
                    if (existing[j].room_no === i) {
                        found = existing[j];
                        break;
                    }
                }
                next.push(found ? found : { room_no: i, bed_count: "2" });
            }
            villa.rooms = next;
        },
        applyLoadedSettings(d) {
            var identical = d.villas_identical !== false;
            var layout = d.room_bed_layout || "2+2+2+3+2+1";
            var villas = [];
            if (d.villas && d.villas.length) {
                for (var i = 0; i < d.villas.length; i++) {
                    var src = d.villas[i];
                    var rooms = [];
                    var srcRooms = src.rooms || [];
                    for (var r = 0; r < srcRooms.length; r++) {
                        rooms.push({
                            room_no: srcRooms[r].room_no,
                            bed_count: String(srcRooms[r].bed_count),
                        });
                    }
                    villas.push({
                        villa_no: src.villa_no,
                        room_count: String(rooms.length),
                        rooms: rooms,
                    });
                }
            } else {
                villas = makeVillas(d.villa_count || 10, parseLayout(layout));
            }
            this.editingId = d.id || this.editingId;
            this.loadedCreatedBy = d.created_by != null ? d.created_by : null;
            this.form = {
                villa_count: String(d.villa_count || villas.length || 10),
                villas_identical: identical,
                room_bed_layout: layout,
                available_rooms_beds: d.available_rooms_beds || "",
                villas: villas,
            };
        },
        loadSettings() {
            var semester = this.currentSemester();
            if (!semester) {
                this.form = emptyForm();
                this.loading = false;
                return;
            }
            this.loading = true;
            this.$apollo
                .query({
                    query: SETTINGS_QUERY,
                    variables: {
                        entity: this.entity,
                        campus: this.campus,
                        semester: semester,
                    },
                    fetchPolicy: "network-only",
                })
                .then((resp) => {
                    var root = resp.data && resp.data.accommodationSettings;
                    if (root && root.data && root.data.id) {
                        this.applyLoadedSettings(root.data);
                    } else {
                        this.editingId = null;
                        this.loadedCreatedBy = null;
                        this.form = emptyForm();
                    }
                })
                .catch(() => {
                    if (this.$swal) {
                        this.$swal.fire({ icon: "error", title: "Error", text: "Failed to load settings." });
                    }
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        save() {
            if (!this.canUpdate) return;
            var semester = this.currentSemester();
            if (!semester) {
                if (this.$swal) {
                    this.$swal.fire({ icon: "warning", title: "Semester required", text: "Enter a semester code before saving." });
                }
                return;
            }
            var villaCount = parseInt(this.form.villa_count, 10);
            if (!villaCount) {
                if (this.$swal) {
                    this.$swal.fire({ icon: "warning", title: "Required fields", text: "Enter the number of villas." });
                }
                return;
            }
            var input = {
                entity: this.entity,
                campus: this.campus,
                semester: semester,
                villas_identical: !!this.form.villas_identical,
                villa_count: villaCount,
                available_rooms_beds: (this.form.available_rooms_beds || "").trim() || null,
            };
            if (this.form.villas_identical) {
                var layout = (this.form.room_bed_layout || "").trim();
                if (!parseLayout(layout).length) {
                    if (this.$swal) {
                        this.$swal.fire({
                            icon: "warning",
                            title: "Layout required",
                            text: "Enter beds per room like 2+2+2+3+2+1.",
                        });
                    }
                    return;
                }
                input.room_bed_layout = layout;
                input.villas = [];
            } else {
                var villas = [];
                var list = this.form.villas || [];
                if (list.length !== villaCount) {
                    if (this.$swal) {
                        this.$swal.fire({ icon: "warning", title: "Required fields", text: "Enter rooms and beds for every villa." });
                    }
                    return;
                }
                for (var i = 0; i < list.length; i++) {
                    var rooms = [];
                    var srcRooms = list[i].rooms || [];
                    if (!srcRooms.length) {
                        if (this.$swal) {
                            this.$swal.fire({
                                icon: "warning",
                                title: "Required fields",
                                text: "Enter rooms for Villa " + list[i].villa_no + ".",
                            });
                        }
                        return;
                    }
                    for (var r = 0; r < srcRooms.length; r++) {
                        var beds = parseInt(srcRooms[r].bed_count, 10);
                        if (!beds) {
                            if (this.$swal) {
                                this.$swal.fire({
                                    icon: "warning",
                                    title: "Required fields",
                                    text: "Enter beds for Villa " + list[i].villa_no + " Room " + srcRooms[r].room_no + ".",
                                });
                            }
                            return;
                        }
                        rooms.push({
                            room_no: srcRooms[r].room_no,
                            bed_count: beds,
                        });
                    }
                    villas.push({
                        villa_no: list[i].villa_no,
                        rooms: rooms,
                    });
                }
                input.villas = villas;
            }
            if (this.calculatedCapacity < 1) {
                if (this.$swal) {
                    this.$swal.fire({ icon: "warning", title: "Capacity", text: "Add at least one bed before saving." });
                }
                return;
            }
            this.saving = true;
            this.$apollo
                .mutate({
                    mutation: UPSERT_MUTATION,
                    variables: { input: input },
                })
                .then((resp) => {
                    var root = resp.data && resp.data.upsertAccommodationSettings;
                    if (root && root.error) {
                        throw new Error(root.message || "Save failed");
                    }
                    if (this.$swal) {
                        var Toast = this.$swal.mixin({
                            toast: true,
                            position: "top-end",
                            showConfirmButton: false,
                            timer: 3000,
                            timerProgressBar: true,
                        });
                        Toast.fire({ icon: "success", title: "Configuration updated." });
                    }
                    this.$emit("changed");
                    if (this.filterAll) {
                        this.backToList();
                    } else if (this.creatingNew) {
                        this.cancelCreate();
                    } else {
                        this.loadSettings();
                    }
                })
                .catch((err) => {
                    var msg = err && err.message ? err.message : "Failed to save settings.";
                    if (this.$swal) {
                        this.$swal.fire({ icon: "error", title: "Error", text: msg });
                    }
                })
                .finally(() => {
                    this.saving = false;
                });
        },
    },
};
</script>

<style lang="scss" scoped>
.ds-wrap {
    background: linear-gradient(180deg, #f8fafc 0%, #ffffff 40%);
}
.ds-toolbar {
    padding: 1.25rem 1rem 1rem;
}
@media (min-width: 640px) {
    .ds-toolbar {
        padding-left: 2rem;
        padding-right: 2rem;
    }
}
.ds-toolbar__row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.75rem;
}
.ds-toolbar__row--between {
    justify-content: space-between;
}
.ds-toolbar__actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-end;
    gap: 0.5rem;
}
.ds-search {
    width: 100%;
    max-width: 20rem;
}
.ds-hint {
    font-size: 0.75rem;
    color: #64748b;
}
.ds-label {
    display: block;
    font-size: 0.75rem;
    font-weight: 600;
    color: #475569;
    margin-bottom: 0.35rem;
    text-align: left;
}
.ds-help {
    margin-top: 0.4rem;
    font-size: 0.75rem;
    color: #64748b;
    line-height: 1.45;
    text-align: left;
}
.ds-input {
    width: 100%;
    border: 1px solid #e2e8f0;
    border-radius: 0.5rem;
    padding: 0.5rem 0.75rem;
    font-size: 0.875rem;
}
.ds-textarea {
    min-height: 5rem;
    resize: vertical;
}
.ds-choice {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
}
.ds-choice__btn {
    border: 1px solid #fdba74;
    background: #fff;
    color: #9a3412;
    border-radius: 9999px;
    padding: 0.4rem 0.9rem;
    font-size: 0.8125rem;
    font-weight: 600;
}
.ds-choice__btn--on {
    background: #ea580c;
    border-color: #ea580c;
    color: #fff;
}
.ds-preview {
    margin-top: 0.75rem;
    background: #fff7ed;
    border: 1px solid #fed7aa;
    border-radius: 0.75rem;
    padding: 0.75rem 1rem;
    text-align: left;
    font-size: 0.8125rem;
    color: #9a3412;
}
.ds-preview__row + .ds-preview__row {
    margin-top: 0.25rem;
}
.ds-villa {
    border: 1px solid #e2e8f0;
    border-radius: 0.85rem;
    padding: 1rem;
    background: #f8fafc;
    text-align: left;
}
.ds-villa + .ds-villa {
    margin-top: 1rem;
}
.ds-villa__title {
    font-size: 0.95rem;
    font-weight: 700;
    color: #0f172a;
    margin-bottom: 0.75rem;
}
.ds-capacity {
    background: linear-gradient(180deg, #fff7ed 0%, #ffffff 100%);
    border: 1px solid #fdba74;
    border-radius: 0.85rem;
    padding: 0.9rem 1rem;
    text-align: left;
}
.ds-capacity__label {
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    color: #c2410c;
}
.ds-capacity__value {
    font-size: 1.5rem;
    font-weight: 800;
    color: #0f172a;
    margin-top: 0.15rem;
}
.ds-capacity__hint {
    font-size: 0.75rem;
    color: #94a3b8;
    margin-top: 0.2rem;
}
.ds-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.5rem 1rem;
    border-radius: 0.5rem;
    font-size: 0.875rem;
    font-weight: 600;
    border: 1px solid transparent;
    cursor: pointer;
    white-space: nowrap;
    transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}
.ds-btn:disabled {
    pointer-events: none;
    opacity: 0.6;
}
.ds-btn--primary {
    background: linear-gradient(180deg, #c2410c 0%, #ea580c 100%);
    color: #fff;
    border: none;
}
.ds-btn--primary:hover {
    background: linear-gradient(180deg, #9a3412 0%, #c2410c 100%);
}
.ds-btn--ghost {
    background: #fff;
    border: 1px solid #e2e8f0;
    color: #475569;
}
.ds-btn--ghost:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
}
.ds-btn--copy {
    background: linear-gradient(180deg, #fff7ed 0%, #ffffff 100%);
    border: 1px solid #fdba74;
    color: #c2410c;
    box-shadow: 0 1px 2px rgba(234, 88, 12, 0.12);
}
.ds-btn--copy:hover {
    background: #ffedd5;
    border-color: #fb923c;
    color: #9a3412;
}
.ds-btn--danger {
    background: #dc2626;
    color: #ffffff;
    border: 1px solid #dc2626;
    box-shadow: 0 1px 2px rgba(220, 38, 38, 0.2);
}
.ds-btn--danger:hover {
    background: #b91c1c;
    border-color: #b91c1c;
}
.ds-table-shell {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 0.85rem;
}
.ds-th {
    padding: 0.75rem 1rem;
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    color: #64748b;
    border-bottom: 1px solid #e2e8f0;
    background: #f8fafc;
}
.ds-td {
    padding: 0.75rem 1rem;
    border-bottom: 1px solid #f1f5f9;
    color: #334155;
}
.ds-row:hover {
    background: #fff7ed;
}
.ds-actions {
    display: inline-flex;
    align-items: center;
    justify-content: flex-end;
    gap: 0.5rem;
}
.ds-icon-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    border-radius: 0.5rem;
    border: 1px solid #e2e8f0;
    background: #fff;
    cursor: pointer;
}
.ds-icon-btn:hover {
    background: #ffedd5;
}
.ds-icon-btn--copy {
    color: #c2410c;
    border-color: #fdba74;
    background: #fff7ed;
}
.ds-icon-btn--copy:hover {
    background: #ffedd5;
    border-color: #fb923c;
    color: #9a3412;
}
.ds-icon-btn--danger {
    color: #dc2626;
}
.ds-icon-btn--danger:hover {
    background: #fef2f2;
    border-color: #fecaca;
}
.ds-empty-icon {
    font-size: 2.5rem;
    margin-bottom: 0.75rem;
}
</style>
